import * as React from "react";

import { Wizard, WizardProps } from "./wizard";

export const useWizard = <T extends object>() => {
    const context = React.useContext(WizardContext);
    if (!context) {
        throw new Error("use wizard must be used inside of a WizardProvider");
    }

    return context as {
        wizardState: T;
        wizardActions: { [key: string]: (...args) => void };
    };
};

export const WizardContext = React.createContext({});

export interface WizardProviderProps<T> {
    data?: T;
    state?: { state: T; actions: { [key: string]: (...args) => void } };
}

export const WizardProvider = <T extends object = any>(
    props: WizardProps & WizardProviderProps<T>
) => {
    if (props.state && props.data) {
        throw new Error(
            "providing both state and data to this provider is not allowed.  please choose one"
        );
    }

    const {
        state: outsideStateProp,
        data: innerDataProp = ({} as Partial<T>) as Required<T>,
        ...rest
    } = props;

    const hasOutsideState = !!outsideStateProp;

    const [innerState, innerSetState] = React.useState<T>(innerDataProp);

    const innerValue = React.useMemo(
        () => {
            const innerAction = (state: T) => {
                innerSetState(state);
            };

            return { wizardState: innerState, wizardActions: { innerAction } };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [innerState]
    ) as {
        wizardState: T;
        wizardActions: { [key: string]: (...args) => void };
    };

    const outsideValue = React.useMemo(() => {
        let outsideState: T = ({} as Partial<T>) as Required<T>;
        let outsideSetState: { [key: string]: (...args) => void };
        if (!!outsideStateProp) {
            outsideState = outsideStateProp.state;
            outsideSetState = outsideStateProp.actions;
        }

        return { wizardState: outsideState, wizardActions: outsideSetState };
    }, [outsideStateProp]) as {
        wizardState: T;
        wizardActions: { [key: string]: (...args) => void };
    };

    return (
        <WizardContext.Provider
            value={hasOutsideState ? outsideValue : innerValue}
        >
            <Wizard {...rest} />
        </WizardContext.Provider>
    );
};
